// Copy for the demo table (MemoryDemoSheet) — merged directly into MemoryStep's hero as the
// pitch's own supporting proof, between the pitch copy and the CTA (see TT-5817 review: "mash
// the two sections together, stay focused on conversion"). Kept separate from MEMORY_STEP_COPY
// below since it's a distinct component. No headline/status of its own anymore — MemoryStep's
// hero heading and subheading carry that framing now, so this table doesn't restate it.
export const MEMORY_DEMO_COPY = {
  pillLabel: 'Filled in from Memory · private',
} as const;

export const MEMORY_STEP_COPY = {
  // The main pitch — headline, one-liner and CTA, all visible without scrolling. Static
  // (unlike the demo sheet below) since it no longer depends on the comparison's phase.
  // Avoid the word "memory" (lowercase) here — "Memory" is the app's name, and a line like
  // "stop writing your timesheet from memory" reads as an instruction about the app it sits
  // right above, not the idiom it's going for. See TT-5817 review.
  heroHeading: 'Stop guessing what you did today.',
  // Shown instead of `heroHeading` to an invited (non-owner) user — see MemoryStep's
  // `isNewWorkspaceOwner` prop. The owner heading leans on evaluation stakes ("stop guessing" —
  // implicitly "or your trial won't prove its case"), which fit someone deciding whether the
  // workspace converts. An invited teammate has no such stakes, just a personal choice about how
  // *they* log time — pushing the same urgency at them reads as nagging rather than helpful. This
  // version states the benefit plainly instead. See TT-5817 review, "less pushy for invited
  // users."
  heroHeadingInvited: 'Let Timely fill in your timesheet for you.',
  // Avoid repeating "guess" from the heading here — read back to back the echo feels like
  // repetition rather than reinforcement. Keeps the "nothing to X, nothing to Y" rhythm instead.
  // Shared between both headings above — it's a plain description of what the app does, not
  // conversion framing, so it doesn't need an invited-specific variant.
  heroSubheading:
    'Install Memory and it captures your hours, projects and notes automatically. Nothing to type, nothing to remember.',
  // OS-dependent CTA copy. `fallback` covers a tracker we don't have specific copy for yet
  // (Linux, Chrome extension) — see the design handoff's open question on unsupported-OS fallback.
  ctaButtonLabel: {
    macOS: 'Install Memory for Mac',
    Windows: 'Install Memory for Windows',
    fallback: 'Install Memory',
  },
  // No download size here — the real numbers (checked against the actual tracker URLs during
  // review) didn't match either placeholder, in both directions, and vary by workspace. Dropping
  // the claim instead of re-pinning it to a number that's just as likely wrong; TT-5817 already
  // tracks "real installer sizes" as blocked on product/infra (see "Not in this PR" in the PR
  // description).
  ctaButtonMeta: {
    macOS: 'macOS 13+ · about 20 seconds',
    Windows: 'Windows 10 and up · about 20 seconds',
    fallback: 'Takes about 20 seconds',
  },
  ctaButtonLabelInstalling: 'Installing Memory…',
  // Shown as a numbered list once the CTA is clicked, until the install-detection poll fires —
  // ported verbatim from the current live MemoryStep (see MemoryStep.tsx on master) per TT-5817
  // review: closes the gap between "click install" and "Memory is running" with the same guidance
  // production already gives, rather than leaving the user staring at a spinner with no context.
  //
  // The Windows download isn't always a zip: `config.ts`'s `memoryWindowsTrackerUrl` serves an
  // `.exe` (`getShouldGetNewMemoryVersion` true) for every workspace except the one named in
  // `WORKSPACES_WITH_ISSUES_WITH_NEW_MEMORY_VERSION` (accounts.ts), which still gets the real S3
  // `.zip`. MemoryStep.tsx picks between `installStepOpenZip`+`installStepRunInstaller` (mac, and
  // that one Windows exception) and `installStepRunExeInstaller` alone (everyone else on Windows —
  // no zip to open, so no separate "open" step) off that same selector. Review originally flagged
  // this as unverifiable from the code; it isn't — `getShouldGetNewMemoryVersion` already decides
  // it, just for the download link, not yet the copy describing it.
  installStepOpenZip: 'Open the downloaded zip file',
  installStepRunInstaller: 'Run the installer (Memory)',
  // Windows, `getShouldGetNewMemoryVersion` true (the common case): the download is a directly
  // runnable `.exe`, so "open the zip, then run the installer" is two steps for something that's
  // actually one — double-clicking the download *is* running the installer.
  installStepRunExeInstaller: 'Run the downloaded installer',
  installStepPermissionsMac: 'Click "Allow" when asked for permissions',
  installStepPermissionsWindows: 'Click "Yes" when asked to allow changes',
  installStepPermissionsDefault: 'Allow the permissions when prompted',
  installStepReturn: 'Return to this page',
  // Social proof — also ported from production (see MemoryStep.tsx on master's `trustText`).
  // A different persuasion lever than the demo table above: the table proves capability, this
  // reduces perceived risk ("other people already trusted this"). Not redundant with it.
  // Owner-only (see MemoryStep's `isNewWorkspaceOwner` prop): market-credibility proof answers
  // "should this company adopt Timely," which is the owner's decision to make, not an invited
  // teammate's — showing it to someone who isn't evaluating the purchase reads as leftover sales
  // copy rather than something relevant to them. See TT-5817 review, "less pushy for invited
  // users."
  trustText: 'Trusted by 5,000+ businesses worldwide',
  // A single compact line, not the full icon+title+body panel the original design used — see
  // TT-5817 review discussion on keeping the pitch above the fold. Not OS-dependent (the claim is
  // about visibility, not the device), unlike the CTA copy above.
  privacyNote:
    'Captured activity is private and visible only to you until you log it or submit your timesheet.',
  // The three-state inline opt-out. Not a modal — see the design handoff for why.
  optIdle: "I'll log time by hand",
  // Trial-gated (see MemoryStep's `isTrial` prop), not owner-gated: only shown to a workspace
  // owner while that workspace is actually on trial.
  optAskConsequence:
    "Then your trial won't show you any of this. Every entry above would be yours to write.",
  // Shown instead of `optAskConsequence` to everyone else: an invited user (any workspace status —
  // it was never their trial to lose) and an owner once the workspace has stopped being a trial
  // (it's no longer "your trial" for them either). Otherwise identical in meaning to the owner
  // copy, just without the word "trial". TT-5817 review: this used to be the one string a whole
  // duplicated legacy Memory/Calendar step existed to keep non-trial owners away from — collapsed
  // to a single trial check on this string instead once that was clear.
  optAskConsequenceInvited:
    'Then none of this fills in for you. Every entry above would be yours to write.',
  optAskInstallInstead: 'Install Memory instead',
  optAskConfirm: "Yes, I'll type them",
  optDoneMessage: 'No problem. You can install Memory any time from Settings.',
  optDoneUndo: "Actually, let's install it",
  // Not part of the approved design — TT-5817 leaves "what 'Yes, I'll type them' persists" open
  // ("Blocked on product" #3). Kept minimal so the step stays usable; revisit once product
  // defines the real mechanism.
  optDoneContinue: 'Continue without installing',
  // Already-installed state: this step's whole job is the pitch, which doesn't apply once
  // Memory is already running. The full post-install experience (device name, first-activity
  // confirmation) is designed in the handoff's parked `3a` option, for a later phase.
  installedHeading: 'Memory is already running.',
  installedSubheading: "You're all set. Continue whenever you're ready.",
  continueAfterInstalling: 'Continue',
} as const;

export const CALENDAR_STEP_COPY = {
  heading: 'Your meetings, already on your Timeline.',
  subheading:
    'Connect your calendar and every meeting appears automatically, named, timed, and dropped straight into your timesheet.',
  // Shown instead of the pair above when the user just declined the Memory pitch
  // (`hasSkippedMemoryInstall`, see MemoryStep.tsx). Reframes this ask as the fallback for
  // automatic capture rather than a second, unrelated demand landing right after a decline —
  // see TT-5817 review on the opt-out's "rhythm": Memory is the priority, Calendar is the next
  // best thing, and the copy should say so instead of repeating a cold open.
  headingAfterSkippingMemory: 'One thing that can still be automatic.',
  subheadingAfterSkippingMemory:
    "You're logging the rest by hand, but meetings don't have to be. Connect your calendar and they'll fill in on their own.",
  // Shown instead of the default pair when the user has Memory connected (installed just now or
  // already running — see CalendarStep.tsx). Leans on the momentum of the "yes" they just gave
  // Memory, and explains why this is a distinct, worthwhile ask rather than a repeat of it: Memory
  // captures app activity, not meeting context. See TT-5817 review.
  headingAfterInstallingMemory: "Nice, let's automate the rest too.",
  subheadingAfterInstallingMemory:
    "Memory's already capturing your hours, projects and notes. Connect your calendar too and your meetings will show up automatically as well.",
  googleCalendarLabel: 'Connect Google Calendar',
  office365Label: 'Connect Office 365',
  calendarSublabel: 'Takes seconds',
  remindMeLater: 'Remind me later',
  // Three variants, same reasoning as the heading/subheading above — the old single string
  // ("Your Timeline will only show app activity") was flatly wrong for someone who'd also skipped
  // Memory: there's no app activity being captured for them to fall back on. Each variant follows
  // onboarding-messaging conventions used by HubSpot/Notion/Intercom-style products: lead with
  // what still works or is true (reassurance before the ask), state the actual gap plainly, one
  // consistent reversibility cue at the end ("we'll remind you") rather than three different ones.
  remindMeLaterWarning:
    "Meetings won't appear on your Timeline until you connect a calendar. We'll remind you once you're in the app.",
  remindMeLaterWarningAfterSkippingMemory:
    "You'll be logging everything by hand, meetings included. We'll remind you once you're in the app.",
  remindMeLaterWarningAfterInstallingMemory:
    "Memory will keep capturing everything else automatically. Only meetings will be manual for now, and we'll remind you once you're in the app.",
  continueWithoutCalendar: 'Continue without calendar',
  continueAfterCalendar: 'Calendar connected! Continue →',
} as const;
